import React from 'react';
import Lottie from 'lottie-react';
import { FiAward } from 'react-icons/fi';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { useTranslation } from 'react-i18next';

import { useTabSettings } from 'utils/hooks/useTabSettings';
import { getFullDate } from 'components/conversations/utils';
import heartLoader from 'assets/heart-loader.json';
import eviePoints from 'assets/images/evi-points-big.webp';

import Layout from 'components/evieOnboarding/Layout';
import BottomNav from 'components/evieApp/BottomNav';
import { EvieAppProvider, useEvieAppContext } from 'components/evieApp/EvieAppProvider';

import './evie-activity.scss';

const ActivityWrapper = () => (
  <EvieAppProvider>
    <EvieActivity />
  </EvieAppProvider>
);

const EvieActivity = () => {
  useTabSettings('Evie', '/evie-favicon.ico');
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { loading, userStats } = useEvieAppContext();
  const { score = 0, streak = 0, streakRecord = 0, activitiesByDay = {} } = userStats;
  const days = Object.keys(activitiesByDay);

  const labelUpdatedContent = (key, params) => {
    switch (key) {
      case 'employee.update_current_day':
      case 'individual.update_current_day':
        return t('evieApp.activity.logs.exerciseSession');

      case 'employee.complete_patient_survey':
      case 'individual.complete_patient_survey':
        return t('evieApp.activity.logs.survey');

      case 'employee.updated_reading_day':
      case 'individual.updated_reading_day':
        return t('evieApp.activity.logs.content.reading');

      case 'employee.program_content_completed':
      case 'individual.program_content_completed':
        return t(`evieApp.activity.logs.content.${params.content_type.toLowerCase()}`);

      case 'employee.program_session_activity_completed':
      case 'individual.program_session_activity_completed':
        return t(`evieApp.activity.logs.${params.activity_type.toLowerCase()}`);

      default:
        return t('evieApp.activity.logs.exerciseSession');
    }
  };

  return (
    <>
      <Layout>
        {!loading && <BottomNav />}
        {!loading && (
          <main className="evie-activity">
            <header className="evie-activity__header">
              <h2 className="evie-profile__header_title">{t('evieApp.activity.title')}</h2>
              <section className="evie-activity__stats">
                <div className="evie-activity__points">
                  <h3 className="evie-activity__points_label">{t('evieApp.activity.header.pointsLabel')}</h3>
                  <p className="evie-activity__points_quantity">
                    {score} {t('evieApp.activity.header.points')}
                  </p>
                  <h3 className="evie-activity__points_label">{t('evieApp.activity.header.streak')}</h3>
                  <p className="evie-activity__points_quantity days">
                    {streak} {t('evieApp.activity.header.days')}
                  </p>
                </div>
                <div className="evie-activity__days">
                  <FiAward className="evie-activity__days_icon" />
                  <p className="evie-activity__days_label">
                    {streakRecord} {t('evieApp.activity.header.days')}
                  </p>
                </div>
              </section>
              <article className="evie-activity__description">
                <IoMdInformationCircleOutline className="evie-activity__description_icon" />
                <p className="evie-activity__description_label">{t('evieApp.activity.header.description')}</p>
              </article>
            </header>
            <section className="evie-activity__logs">
              <ul className="evie-activity__dates">
                {days.map((day) => {
                  const dayLabel = getFullDate({
                    date: new Date(day),
                    lng: language,
                    options: { month: 'long', day: 'numeric' },
                  });
                  const logs = activitiesByDay[day];

                  return (
                    <li key={day}>
                      <p className="evie-activity__dates_date">{dayLabel}</p>
                      <ul className="evie-activity__activities">
                        {logs.map((log) => {
                          const { id = '', key = '', parameters = {} } = log;
                          return (
                            <li key={id} className="evie-activity__activities_activity">
                              <p>{labelUpdatedContent(key, parameters)}</p>
                              <div>
                                <span>+{parameters.points}</span>
                                <img src={eviePoints} alt="Evie points" />
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  );
                })}
              </ul>
            </section>
          </main>
        )}
      </Layout>
      {loading && (
        <div className="onboarding-layout__lottie">
          <Lottie animationData={heartLoader} loop style={{ width: 140, height: 110 }} />
        </div>
      )}
    </>
  );
};

export default ActivityWrapper;
